// ── 멀티테넌트 SaaS — 테넌트 관리 (Phase 1 control plane) ─────────────────────
// 탭: 테넌트 / 회사 / 구독 / 요금제(2026-10-25). 화면 전체가 운영자 전용. 구독·요금제 탭은 SubscriptionTab·PlansTab.
//   2026-10-20: 테넌트 메뉴는 테넌트 줄을 누르면 펼쳐지는 '메뉴'(TenantMenuPanel)에서 정한다 — 예전 '모듈(메뉴)' 탭은 없앰(사용자 결정).
import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../../api/apiClient';
import { useToast } from '../../../components/Toast';
import TenantMenuPanel from './TenantMenuPanel';
import TenantAdminPanel from './TenantAdminPanel';
import TenantBrandingPanel from './TenantBrandingPanel';
import { isAdminUser } from '../adminlayout/sidebarFilters';
import CompaniesTab from './CompaniesTab';
import SubscriptionTab from './SubscriptionTab';
import PlansTab from './PlansTab';
import { input, btn, btnOutline, card, th, td, statusBadge } from './saasShared';
import {
  stdHeaderRow, stdPageTitle, stdPageSubtitle, stdTabGroup, stdTabBtn,
} from '../adminlayout/designStandard';

const MOBILE_BREAKPOINT = 768;

const TABS = [
  { key: 'tenants', label: '테넌트' },
  { key: 'companies', label: '회사' },
  { key: 'subscription', label: '구독' },
  { key: 'plans', label: '요금제' }, // 2026-10-25 요금제 4-2
];

export default function SaasTenantManagement() {
  const { showToast } = useToast();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [tab, setTab] = useState('tenants');
  const [tenants, setTenants] = useState([]);
  const [selected, setSelected] = useState('');
  const [newTenant, setNewTenant] = useState({ code: '', name: '' });
  const [adminPanelTenant, setAdminPanelTenant] = useState(''); // 관리자 패널이 열린 테넌트 code (토글)
  const [menuTenant, setMenuTenant] = useState(''); // 메뉴가 펼쳐진 테넌트 code (줄 누르면 토글, 2026-10-20)
  const [brandingTenant, setBrandingTenant] = useState(''); // 브랜딩 패널이 열린 테넌트 code (토글)
  const isOperator = isAdminUser(); // 운영사(admin)=true / 테넌트 관리자(employee+TENANT_ADMIN)=false
  // 이 화면은 라우트(AppRoutes)·메뉴(menuConfig SAAS_TENANT_MANAGEMENT) 모두 운영자 전용이라
  //   테넌트 관리자 분기는 도달 불가 죽은 코드였다(2026-08-17 감사) — 제거. 테넌트 관리자의
  //   회사 모듈 관리는 업무/기능 배정 화면(고객별 기능 배정)에서 한다.
  const visibleTabs = TABS;

  const loadTenants = useCallback(async () => {
    const res = await apiClient.get('/api/admin/saas/tenants');
    if (res.ok) {
      const rows = res.data?.data || [];
      setTenants(rows);
      setSelected((prev) => prev || rows[0]?.code || '');
    }
  }, []);
  // 요금제 목록 + 테넌트마다 요금제·'요금제와 다름'·인원 한도(2026-10-25 요금제 4-2)
  const [planInfo, setPlanInfo] = useState(null);
  const loadPlans = useCallback(async () => {
    const res = await apiClient.get('/api/admin/saas/plans');
    if (res.ok) setPlanInfo(res.data?.data || null);
  }, []);
  const reloadAll = useCallback(async () => { await Promise.all([loadTenants(), loadPlans()]); }, [loadTenants, loadPlans]);
  useEffect(() => { reloadAll(); }, [reloadAll]);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function createTenant() {
    if (!newTenant.code.trim() || !newTenant.name.trim()) { showToast('코드와 이름을 입력하세요.', 'error'); return; }
    const res = await apiClient.post('/api/admin/saas/tenants', { code: newTenant.code, name: newTenant.name });
    if (!res.ok) { showToast(res.data?.message || '생성 실패', 'error'); return; }
    showToast('테넌트를 생성했습니다.', 'success');
    setNewTenant({ code: '', name: '' });
    await loadTenants();
  }
  async function setTenantStatus(code, status) {
    const res = await apiClient.put(`/api/admin/saas/tenants/${code}`, { status });
    if (!res.ok) { showToast(res.data?.message || '변경 실패', 'error'); return; }
    showToast('테넌트 상태를 변경했습니다.', 'success');
    await loadTenants();
  }

  return (
    <div style={{ maxWidth: 1100, color: 'var(--text)', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* ── 페이지 헤더: 직원 관리 표준(텍스트 제목 + 부제) ── */}
      <div style={stdHeaderRow(isMobile)}>
        <div>
          <h2 style={stdPageTitle}>테넌트 관리</h2>
          <div style={stdPageSubtitle}>테넌트·회사·모듈을 관리합니다.</div>
        </div>
      </div>
      <div style={{ ...stdTabGroup, maxWidth: '100%', overflowX: 'auto' }}>
        {visibleTabs.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} style={stdTabBtn(tab === t.key)}>{t.label}</button>
        ))}
      </div>

      {tab === 'tenants' && (
        <>
          {isOperator && (
            <div style={{ ...card, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: 13 }}>새 테넌트</strong>
              <input style={{ ...input, width: 140 }} placeholder="코드(영문대문자)" value={newTenant.code} onChange={(e) => setNewTenant({ ...newTenant, code: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })} />
              <input style={{ ...input, flex: 1, minWidth: 160 }} placeholder="이름" value={newTenant.name} onChange={(e) => setNewTenant({ ...newTenant, name: e.target.value })} />
              <button style={btn} onClick={createTenant}>추가</button>
            </div>
          )}
          <div style={card}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr><th style={{ ...th, width: 22 }}></th><th style={th}>코드</th><th style={th}>이름</th><th style={th}>유형</th><th style={th}>상태</th><th style={th}>구독</th><th style={th}>회사</th><th style={th}>메뉴</th><th style={th}></th></tr></thead>
              <tbody>
                {tenants.map((t) => (
                  <React.Fragment key={t.code}>
                    <tr
                      onClick={() => setMenuTenant((p) => (p === t.code ? '' : t.code))}
                      style={{ cursor: 'pointer', background: menuTenant === t.code ? 'var(--bg-subtle)' : 'transparent' }}
                    >
                      <td style={{ ...td, color: 'var(--text-muted)', textAlign: 'center' }}>{menuTenant === t.code ? '▾' : '▸'}</td>
                      <td style={td}>{t.code}</td>
                      <td style={td}>{t.name}</td>
                      <td style={td}>{t.tenant_type === 'platform' ? '플랫폼' : '고객사'}</td>
                      <td style={td}>{statusBadge(t.status)}</td>
                      <td style={td}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                          {statusBadge(t.subscription_status)}
                          {planInfo?.tenants?.[t.code]?.plan_name && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{planInfo.tenants[t.code].plan_name}</span>}
                          {planInfo?.tenants?.[t.code]?.differs && <span style={{ fontSize: 11, fontWeight: 700, color: '#b45309' }}>요금제와 다름</span>}
                          {planInfo?.tenants?.[t.code]?.expired_days > 0 && <span style={{ fontSize: 11, fontWeight: 700, color: '#dc2626' }}>기간 지남</span>}
                        </div>
                      </td>
                      <td style={td}>{t.company_count}</td>
                      <td style={td}>{t.menus_on ?? t.modules_on}</td>
                      <td style={td} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          <button style={{ ...btnOutline, ...(brandingTenant === t.code ? { background: 'var(--std-pill-on-bg, rgba(59,154,242,0.12))', color: 'var(--std-pill-on-text, #1d7de6)' } : {}) }} onClick={() => setBrandingTenant((p) => (p === t.code ? '' : t.code))}>브랜딩</button>
                          {t.tenant_type !== 'platform' && (
                            <>
                              <button style={{ ...btnOutline, ...(adminPanelTenant === t.code ? { background: 'var(--std-pill-on-bg, rgba(59,154,242,0.12))', color: 'var(--std-pill-on-text, #1d7de6)' } : {}) }} onClick={() => setAdminPanelTenant((p) => (p === t.code ? '' : t.code))}>관리자</button>
                              {t.status === 'active'
                                ? <button style={btnOutline} onClick={() => setTenantStatus(t.code, 'inactive')}>비활성</button>
                                : <button style={btnOutline} onClick={() => setTenantStatus(t.code, 'active')}>활성</button>}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                    {menuTenant === t.code && (
                      <tr>
                        <td style={{ ...td, background: 'var(--bg-subtle)', padding: 12 }} colSpan={9}>
                          <TenantMenuPanel tenantCode={t.code} tenantName={t.name} isPlatform={t.tenant_type === 'platform'} showToast={showToast} onChanged={reloadAll} />
                        </td>
                      </tr>
                    )}
                    {brandingTenant === t.code && (
                      <tr>
                        <td style={{ ...td, background: 'var(--bg-subtle)', padding: 12 }} colSpan={9}>
                          <TenantBrandingPanel tenant={t} showToast={showToast} onSaved={loadTenants} />
                        </td>
                      </tr>
                    )}
                    {adminPanelTenant === t.code && (
                      <tr>
                        <td style={{ ...td, background: 'var(--bg-subtle)', padding: 12 }} colSpan={9}>
                          <TenantAdminPanel tenantCode={t.code} isOperator={isOperator} showToast={showToast} />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'companies' && (
        <CompaniesTab tenants={tenants} selected={selected} setSelected={setSelected} showToast={showToast} onChanged={loadTenants} isOperator={isOperator} />
      )}

      {tab === 'subscription' && (
        <SubscriptionTab tenants={tenants} selected={selected} setSelected={setSelected} showToast={showToast} onChanged={reloadAll} planInfo={planInfo} />
      )}

      {tab === 'plans' && (
        <PlansTab planInfo={planInfo} showToast={showToast} onChanged={reloadAll} />
      )}
    </div>
  );
}
